import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, ArrowRight, Upload, Brain, Shield, Building2, Heart, School, Zap, CheckCircle } from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/hxjiycyn_Hero.jpg",
  card1: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/sb86xbyt_card-1.jpg",
  features: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/f7ip8czd_features.png",
  hospital: "/who-we-help-hospital.png",
  ngo: "/who-we-help-ngo.png",
  diagnostic: "/who-we-help-diagnostic.png",
  school: "/who-we-help-school.png",
};

const features = [
  { icon: Upload, title: "Single-photo X-Ray upload", description: "Upload a left-hand posteroanterior radiograph from your phone or DICOM viewer. Our pipeline handles compression and image normalisation automatically.", color: "#4ECDC4" },
  { icon: Brain, title: "AI bone-age prediction", description: "An ensemble neural network (EfficientNetV2-S backbone) trained on tens of thousands of expert-annotated hand X-rays returns skeletal maturity within 0.5 years.", color: "#A855F7" },
  { icon: Zap, title: "Seconds, not days", description: "Bone-age assessment that traditionally takes radiologists 10-20 minutes per X-ray completes in under 8 seconds — no manual atlas matching.", color: "#FFE66D" },
  { icon: CheckCircle, title: "Consistency check", description: "Flags whether the predicted bone age is consistent with the child's chronological age, helping pediatricians spot early/delayed development at a glance.", color: "#FF6B6B" },
  { icon: Shield, title: "Branded clinical PDF", description: "Generate a comprehensive report with the original X-ray, prediction metrics, and clinician disclaimer — ready for parent consultation or referral.", color: "#4ECDC4" },
];

const whoWeHelp = [
  { icon: Building2, title: "Hospitals", description: "Integrated bone-age assessment for pediatric departments", image: IMAGES.hospital },
  { icon: Heart, title: "NGOs", description: "Large-scale child screening for underserved communities", image: IMAGES.ngo },
  { icon: Activity, title: "Diagnostic Centers", description: "AI-augmented bone-age reports for faster turnaround", image: IMAGES.diagnostic },
  { icon: School, title: "Schools", description: "Annual growth & development screening programs", image: IMAGES.school },
];

const howItWorks = [
  { step: "01", title: "Capture X-Ray", description: "Take a standard left-hand posteroanterior radiograph using any clinical X-ray machine." },
  { step: "02", title: "Upload to tots360", description: "Upload from your phone, tablet or workstation. Auto-compression keeps the image transfer safe and fast." },
  { step: "03", title: "Get AI Bone Age", description: "Our deep-learning model returns predicted bone age + chronological-age comparison in seconds." },
  { step: "04", title: "Download Clinical Report", description: "Branded PDF with X-ray thumbnail, all key metrics and disclaimer — perfect for parent consultation." },
];

const BoneAgeAboutPage = () => {
  useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="pt-28 pb-16">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <div className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center bg-[#4ECDC4]/10 border border-[#4ECDC4]/20">
                <Activity className="w-8 h-8 text-[#4ECDC4]" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4" data-testid="bone-age-about-title">
                Bone Age <span className="text-[#4ECDC4]">Prediction</span>
              </h1>
              <p className="text-lg text-white/50 mb-4">AI-powered skeletal maturity assessment from a single hand X-ray — in seconds.</p>
              <p className="text-base text-white/40 leading-relaxed mb-8">
                The fastest way to determine a child&apos;s bone age. Upload a left-hand radiograph and our deep-learning ensemble returns
                a calibrated skeletal age with a consistency check against the child&apos;s chronological age — empowering pediatricians,
                radiologists and screening programs to spot early- or delayed-development concerns the same day.
              </p>
              <Link to="/tools/bone-age" className="inline-flex items-center gap-2 bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold px-8 py-4 rounded-xl text-lg transition-colors" data-testid="bone-age-try-now-btn">
                Try Bone Age Prediction <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.hero} alt="Bone Age Prediction" className="w-full h-80 object-cover rounded-xl" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Card */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="glass-card overflow-hidden grid md:grid-cols-2 gap-0">
            <img src={IMAGES.card1} alt="AI-driven X-ray analysis" className="w-full h-80 object-cover" />
            <div className="p-8 flex flex-col justify-center">
              <h3 className="font-display text-2xl text-white mb-3">From X-Ray to Bone Age in 8 seconds</h3>
              <p className="text-white/55 leading-relaxed text-sm">
                Traditional manual atlas reading (Greulich-Pyle / Tanner-Whitehouse) takes 10-20 minutes per X-ray and shows
                significant inter-radiologist variability. Our AI delivers consistent, calibrated results in under 8 seconds
                — making expert-grade skeletal-age assessment accessible to every clinic, school screening drive and diagnostic centre.
              </p>
              <div className="h-1 w-12 rounded-full mt-4 bg-[#4ECDC4]" />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.features} alt="Features" className="w-full rounded-xl" />
              </div>
            </motion.div>
            <div>
              <h2 className="font-display text-3xl text-white mb-6">What this tool offers</h2>
              <div className="space-y-5">
                {features.map((f, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-4" data-testid={`bone-age-feature-${i}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${f.color}15` }}>
                      <f.icon className="w-5 h-5" style={{ color: f.color }} />
                    </div>
                    <div>
                      <h3 className="font-display text-base text-white mb-1">{f.title}</h3>
                      <p className="text-sm text-white/45 leading-relaxed">{f.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-white mb-3">How It Works</h2>
            <p className="text-white/40">Four simple steps from X-ray to clinical PDF</p>
          </div>
          <div className="space-y-5">
            {howItWorks.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex items-start gap-5 glass-card p-5" data-testid={`bone-age-step-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#4ECDC4]/10 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-[#4ECDC4]">{s.step}</span>
                </div>
                <div>
                  <h3 className="font-display text-lg text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-white/50">{s.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why */}
        <section className="max-w-4xl mx-auto px-6 mb-20" data-testid="why-bone-age-section">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-6 text-center">Why bone age matters</h2>
          <div className="space-y-4 text-white/55 leading-relaxed text-base md:text-lg">
            <p>
              A child&apos;s <strong className="text-white/80">bone age</strong> can differ from their calendar age by up to 2 years —
              and that gap is the single most useful early indicator of growth-hormone deficiency, premature puberty and
              skeletal-maturation issues. A timely <strong className="text-white/80">bone age test</strong> helps pediatricians
              decide whether a child is on a healthy trajectory or needs closer follow-up.
            </p>
            <p>
              Bone-age results from this tool feed directly into our companion <Link to="/tools/growth" className="text-[#4ECDC4] underline">Growth Assessment</Link>
              {" "}tool for adult-height prediction and Growth Health Index scoring.
            </p>
          </div>
        </section>

        {/* Who We Help */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-white mb-3">Who We Help</h2>
            <p className="text-white/40">Trusted across pediatric care and screening programs</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoWeHelp.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="glass-card overflow-hidden group">
                  <img src={item.image} alt={item.title} className="w-full h-44 object-contain bg-[#0A1029] p-2 group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[#4ECDC4]" />
                      <h3 className="font-display text-base text-white">{item.title}</h3>
                    </div>
                    <p className="text-xs text-white/45">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 text-center">
          <Link to="/tools/bone-age" className="inline-flex items-center gap-2 bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold px-10 py-4 rounded-xl text-lg transition-colors">
            Start Bone Age Prediction <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BoneAgeAboutPage;
